import { useState, useRef } from "react";
import {
  X,
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  Check,
  Info,
  AlertCircle,
  FileUp,
} from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { errorMessage } from "../../lib/format";

const EXPECTED_COLUMNS = ["Description", "Product", "Quantity", "Unit", "Value/Price (Optional)"];

const SAMPLE_ORDERS_DATA = [
  { Description: "Matte finish 350 GSM cards", Product: "Visiting Cards", Quantity: 500, Unit: "pcs", "Value/Price (Optional)": 2500 },
  { Description: "Tri-fold 4-color company brochure", Product: "Brochure", Quantity: 1000, Unit: "pcs", "Value/Price (Optional)": "" },
  { Description: "Custom printed corrugated box", Product: "Packaging Box", Quantity: 250, Unit: "boxes", "Value/Price (Optional)": 15000 },
];

function formatCurrency(amount) {
  if (amount === null || amount === undefined || amount === "" || isNaN(amount)) return "—";
  if (Number(amount) === 0) return "₹0";
  return `₹${Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function cleanKey(k) {
  return String(k || "").trim().toLowerCase().replace(/[\s._/\\-]/g, "");
}

export default function BulkLeadOrderUploadModal({ lead, isOpen, onClose, onCreated }) {
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [headerWarning, setHeaderWarning] = useState("");
  const fileInputRef = useRef(null);

  if (!isOpen || !lead) return null;

  // Download Sample Excel (.xlsx) / CSV (.csv) Template
  const handleDownloadSample = (format = "xlsx") => {
    try {
      if (format === "csv") {
        const csvContent =
          "Description,Product,Quantity,Unit,Value/Price\n" +
          "Matte finish 350 GSM cards,Visiting Cards,500,pcs,2500\n" +
          "Tri-fold 4-color company brochure,Brochure,1000,pcs,\n" +
          "Custom printed corrugated box,Packaging Box,250,boxes,15000\n";
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `sample_lead_orders_${lead.lead_id}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        const ws = XLSX.utils.json_to_sheet(SAMPLE_ORDERS_DATA);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Lead Orders");
        XLSX.writeFile(wb, `sample_lead_orders_${lead.lead_id}.xlsx`);
      }
      toast.success(`Downloaded sample orders ${format.toUpperCase()} template`);
    } catch (err) {
      toast.error("Failed to generate sample template");
    }
  };

  // Parse Excel / CSV file
  const processFile = (selectedFile) => {
    if (!selectedFile) return;

    const ext = selectedFile.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      toast.error("Please upload a valid .xlsx or .csv file");
      return;
    }

    setFile(selectedFile);
    setParsing(true);
    setHeaderWarning("");
    setParsedRows([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
          setHeaderWarning("The workbook has no sheets");
          setParsing(false);
          return;
        }

        const worksheet = workbook.Sheets[firstSheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (!json || json.length === 0) {
          setHeaderWarning("The uploaded file contains no data rows.");
          setParsedRows([]);
          setParsing(false);
          return;
        }

        // Validate and normalize rows
        const validated = [];
        let rowIdx = 0;

        for (const row of json) {
          rowIdx++;
          const getVal = (aliases) => {
            for (const k of Object.keys(row)) {
              const cK = cleanKey(k);
              if (aliases.some((a) => cleanKey(a) === cK)) {
                return String(row[k] || "").trim();
              }
            }
            return "";
          };

          const rawDescription = getVal(["description", "desc", "details", "specifications", "spec"]);
          const rawProduct = getVal(["product", "product name", "item", "item description", "name"]);
          const rawQty = getVal(["quantity", "qty.", "qty", "count"]);
          const rawUnit = getVal(["unit", "units", "uom", "unit of measurement", "measurement"]);
          const rawValue = getVal(["value/price (optional)", "value/price", "value", "price", "amount", "total", "rate", "unit price"]);

          // Skip completely empty rows
          if (!rawDescription && !rawProduct && !rawQty && !rawUnit && !rawValue) {
            continue;
          }

          let description = String(rawDescription || "").trim();
          let product = String(rawProduct || "").trim();

          // If product is empty but description has text (e.g. older files with just "Item Description")
          if (!product && description) {
            product = description;
            description = "";
          }

          const qtyParsed = parseFloat(rawQty);
          const quantity = isNaN(qtyParsed) || qtyParsed <= 0 ? "" : qtyParsed;
          const unit = String(rawUnit || "").trim() || "pcs";
          const rawValStr = String(rawValue !== undefined && rawValue !== null ? rawValue : "").trim();
          const value = rawValStr === "" ? null : (isNaN(parseFloat(rawValStr)) ? "" : parseFloat(rawValStr));

          const errors = [];
          if (!product) errors.push("Product name is required");
          if (quantity === "" || quantity <= 0) errors.push("Quantity must be > 0");
          if (rawValStr !== "" && (value === "" || value < 0)) errors.push("Value/Price must be ≥ 0");

          validated.push({
            rowNumber: rowIdx,
            description,
            product,
            quantity: quantity === "" ? 1 : quantity,
            unit,
            value: value === "" ? null : value,
            hasPrice: value !== null && !isNaN(value) && value > 0,
            isValid: errors.length === 0,
            errors,
          });
        }

        if (validated.length === 0) {
          setHeaderWarning("No valid order items found in the uploaded file.");
        }

        setParsedRows(validated);
      } catch (err) {
        setHeaderWarning("Failed to parse file. Please verify file format and structure.");
        toast.error("Failed to parse the file");
      } finally {
        setParsing(false);
      }
    };

    reader.readAsBinaryString(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleReset = () => {
    setFile(null);
    setParsedRows([]);
    setHeaderWarning("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validRows = parsedRows.filter((r) => r.isValid);
  const invalidRows = parsedRows.filter((r) => !r.isValid);
  const totalValue = validRows.reduce((sum, r) => sum + (Number(r.value) || 0), 0);

  // Add Orders Action
  const handleAddOrders = async () => {
    if (submitting) return;
    if (validRows.length === 0) {
      toast.error("No valid order items to add");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        orders: validRows.map((r) => ({
          description: r.description,
          product: r.product,
          quantity: r.quantity,
          unit: r.unit,
          value: r.value !== null && !isNaN(r.value) ? Number(r.value) : null,
        })),
      };

      const res = await api("post", `/leads/${lead.lead_id}/orders/bulk`, payload);
      const count = res.data?.created_count || validRows.length;
      toast.success(`Successfully added ${count} orders to ${lead.company_name}`);

      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to add orders"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      style={{
        zIndex: 10000,
        backgroundColor: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(4px)",
      }}
      data-testid="bulk-lead-order-modal-backdrop"
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 680,
          width: "95%",
          padding: "24px 28px",
          borderRadius: 14,
          boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(15, 23, 42, 0.06)",
        }}
        data-testid="bulk-lead-order-modal"
      >
        {/* Modal Head */}
        <div className="modal-head" style={{ marginBottom: 16, alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                border: "1px solid #bfdbfe",
                color: "#2563eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 6px rgba(37, 99, 235, 0.12)",
                flexShrink: 0,
              }}
            >
              <FileSpreadsheet size={20} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: "#2563eb",
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                  marginBottom: 2,
                }}
              >
                LEAD ORDERS · BULK UPLOAD
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#0f172a",
                  letterSpacing: "-0.3px",
                }}
              >
                Upload Multiple Orders
              </h2>
              <div
                style={{
                  fontSize: 12,
                  color: "#64748b",
                  marginTop: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>Lead:</span>
                <span
                  style={{
                    fontWeight: 600,
                    color: "#1e293b",
                    background: "#f1f5f9",
                    padding: "2px 8px",
                    borderRadius: 4,
                  }}
                >
                  {lead.company_name}
                </span>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>({lead.lead_id})</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            data-testid="close-bulk-lead-order-modal"
            style={{
              padding: 6,
              borderRadius: 6,
              color: "#64748b",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              cursor: "pointer",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Lead Order Separation Notice */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "11px 14px",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: 8,
            color: "#166534",
            fontSize: 12.5,
            lineHeight: 1.45,
            marginBottom: 16,
          }}
        >
          <Info size={17} style={{ flexShrink: 0, marginTop: 1, color: "#16a34a" }} />
          <div>
            <b style={{ color: "#14532d" }}>Separate Lead Orders:</b> These items will remain attached
            to this Lead and will automatically move to the main Orders section once the Lead is
            converted to a Client.
          </div>
        </div>

        {/* Template Download & Columns Bar */}
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div style={{ fontSize: 12, color: "#64748b" }}>
            File columns:{" "}
            {EXPECTED_COLUMNS.map((col, idx) => (
              <span key={col}>
                <b style={{ color: "#1e293b", background: "#ffffff", padding: "1px 6px", borderRadius: 4, border: "1px solid #e2e8f0" }}>{col}</b>
                {idx < EXPECTED_COLUMNS.length - 1 ? "  " : ""}
              </span>
            ))}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="outline-btn compact"
              onClick={() => handleDownloadSample("xlsx")}
              data-testid="download-lead-order-sample-xlsx-btn"
              style={{
                height: 28,
                fontSize: 11.5,
                fontWeight: 600,
                padding: "0 10px",
                borderRadius: 6,
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#334155",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                cursor: "pointer",
              }}
              title="Download Sample Excel File (.xlsx)"
            >
              <Download size={12} /> Sample (.xlsx)
            </button>
            <button
              type="button"
              className="outline-btn compact"
              onClick={() => handleDownloadSample("csv")}
              data-testid="download-lead-order-sample-csv-btn"
              style={{
                height: 28,
                fontSize: 11.5,
                fontWeight: 600,
                padding: "0 10px",
                borderRadius: 6,
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#334155",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                cursor: "pointer",
              }}
              title="Download Sample CSV File (.csv)"
            >
              <Download size={12} /> Sample (.csv)
            </button>
          </div>
        </div>

        {/* Upload Dropzone */}
        {!file && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            style={{
              border: dragOver ? "2px dashed #2563eb" : "2px dashed #cbd5e1",
              background: dragOver ? "#eff6ff" : "#fbfcfe",
              borderRadius: 10,
              padding: "28px 20px",
              textAlign: "center",
              cursor: "pointer",
              marginBottom: 16,
              transition: "all 0.15s ease",
            }}
            data-testid="bulk-lead-order-dropzone"
          >
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              style={{ display: "none" }}
              data-testid="bulk-lead-order-file-input"
            />
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: "#eff6ff",
                color: "#2563eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 10px",
              }}
            >
              <Upload size={22} />
            </div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a", marginBottom: 3 }}>
              Click to browse or drag & drop Excel / CSV file
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              Expected columns: <b>Description, Product, Quantity, Unit, Value/Price (Optional)</b>
            </div>
          </div>
        )}

        {/* Selected File Strip */}
        {file && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 14px",
              background: "#f1f5f9",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              marginBottom: 14,
              fontSize: 12.5,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FileSpreadsheet size={18} style={{ color: "#2563eb" }} />
              <b style={{ color: "#0f172a" }}>{file.name}</b>
              <span style={{ color: "#64748b", fontSize: 11.5 }}>
                ({(file.size / 1024).toFixed(1)} KB)
              </span>
            </div>
            <button
              type="button"
              className="outline-btn compact"
              onClick={handleReset}
              data-testid="bulk-lead-order-reset-file-btn"
              style={{
                height: 28,
                fontSize: 11.5,
                fontWeight: 600,
                padding: "0 10px",
                borderRadius: 6,
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#475569",
                cursor: "pointer",
              }}
            >
              Choose different file
            </button>
          </div>
        )}

        {/* Header Warning Alert */}
        {headerWarning && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "10px 14px",
              borderRadius: 8,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#b91c1c",
              fontSize: 12.5,
              marginBottom: 14,
            }}
          >
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>{headerWarning}</div>
          </div>
        )}

        {/* Loading */}
        {parsing && (
          <div style={{ padding: 24, textAlign: "center", color: "#64748b", fontSize: 13 }}>
            Reading uploaded orders...
          </div>
        )}

        {/* Preview Table */}
        {!parsing && parsedRows.length > 0 && (
          <div>
            {/* Status Banner */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "9px 12px",
                background: invalidRows.length > 0 ? "#fffbeb" : "#ecfdf5",
                border: invalidRows.length > 0 ? "1px solid #fde68a" : "1px solid #bbf7d0",
                borderRadius: 6,
                fontSize: 12.5,
                marginBottom: 12,
              }}
            >
              <div>
                <b style={{ color: "#15803d" }}>{validRows.length} order items</b> ready to add
                {invalidRows.length > 0 && (
                  <span style={{ color: "#b45309", marginLeft: 6 }}>
                    ({invalidRows.length} invalid rows will be skipped)
                  </span>
                )}
              </div>
              <div style={{ fontWeight: 600, color: "#0f172a" }}>
                Total Value: <strong style={{ color: "#15803d" }}>{formatCurrency(totalValue)}</strong>
              </div>
            </div>

            {/* Table */}
            <div
              style={{
                maxHeight: 250,
                overflowY: "auto",
                border: "1.5px solid #cbd5e1",
                borderRadius: 8,
                marginBottom: 18,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead>
                  <tr
                    style={{
                      background: "#f8fafc",
                      borderBottom: "1.5px solid #cbd5e1",
                      textAlign: "left",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                    }}
                  >
                    <th style={{ padding: "8px 10px", fontWeight: 600, color: "#64748b", fontSize: 11.5, width: 35 }}>#</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600, color: "#64748b", fontSize: 11.5, minWidth: 150 }}>Description</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600, color: "#64748b", fontSize: 11.5, width: 130 }}>Product</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600, color: "#64748b", fontSize: 11.5, width: 85 }}>Quantity</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600, color: "#64748b", fontSize: 11.5, width: 70 }}>Unit</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600, color: "#64748b", fontSize: 11.5, width: 110 }}>Value/Price</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600, color: "#64748b", fontSize: 11.5, width: 85 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.map((r) => (
                    <tr
                      key={r.rowNumber}
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: r.isValid ? "#ffffff" : "#fef2f2",
                      }}
                      data-testid={`bulk-lead-order-preview-row-${r.rowNumber}`}
                    >
                      <td style={{ padding: "8px 10px", color: "#94a3b8", fontSize: 11.5 }}>{r.rowNumber}</td>
                      <td style={{ padding: "8px 10px", color: "#475569", fontSize: 12, minWidth: 150 }}>
                        {r.description || <span style={{ color: "#94a3b8" }}>—</span>}
                      </td>
                      <td style={{ padding: "8px 10px", fontWeight: 500, color: "#0f172a", width: 130 }}>
                        {r.product || <span style={{ color: "#b91c1c", fontStyle: "italic" }}>Missing product</span>}
                      </td>
                      <td style={{ padding: "8px 10px", color: "#0f172a", fontWeight: 600 }}>
                        {typeof r.quantity === "number" ? r.quantity.toLocaleString() : (r.quantity || "—")}
                      </td>
                      <td style={{ padding: "8px 10px", color: "#64748b" }}>{r.unit}</td>
                      <td style={{ padding: "8px 10px", fontWeight: 600, color: r.hasPrice ? "#15803d" : "#64748b" }}>
                        {r.hasPrice ? formatCurrency(r.value) : <span style={{ color: "#94a3b8", fontWeight: 400 }}>— (Optional)</span>}
                      </td>
                      <td style={{ padding: "8px 10px", fontSize: 11.5 }}>
                        {r.isValid ? (
                          <span
                            style={{
                              color: "#15803d",
                              fontWeight: 600,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 3,
                            }}
                          >
                            <Check size={13} /> Valid
                          </span>
                        ) : (
                          <span style={{ color: "#b91c1c", fontSize: 11 }} title={r.errors.join(", ")}>
                            ✗ {r.errors[0]}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 10,
            paddingTop: 16,
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <button
            type="button"
            className="outline-btn"
            onClick={onClose}
            disabled={submitting}
            data-testid="bulk-lead-order-cancel-btn"
            style={{
              height: 38,
              fontSize: 13,
              fontWeight: 600,
              padding: "0 18px",
              borderRadius: 8,
              border: "1.5px solid #cbd5e1",
              background: "#ffffff",
              color: "#475569",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-btn"
            disabled={submitting || validRows.length === 0}
            onClick={handleAddOrders}
            data-testid="bulk-lead-order-confirm-btn"
            style={{
              height: 38,
              fontSize: 13,
              fontWeight: 600,
              padding: "0 20px",
              borderRadius: 8,
              background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              border: "none",
              color: "#ffffff",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              boxShadow: "0 2px 8px rgba(37, 99, 235, 0.25)",
              cursor: submitting || validRows.length === 0 ? "not-allowed" : "pointer",
            }}
          >
            <CheckCircle2 size={16} />
            {submitting ? "Adding Orders..." : `Add Orders (${validRows.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
